import { AllocationMember } from '../types/allocation';
import { ProjectCost } from '../types/api';
import apiClient from './apiClient';

export interface DashboardData {
  id?: number;
  projectId: number;
  label?: string;
  ownerId?: number;
  projectName?: string;
  customerName?: string;
  allocationMembers: AllocationMember[];
  projectCosts: ProjectCost[];
  calculatedCosts: {
    totalEmployeeCosts: number;
    totalProjectCosts: number;
    totalCost: number;
    billableCosts: number;
    nonBillableCosts: number;
    /** Quote margin on revenue. Absent on dashboards saved before this existed. */
    profitPercent?: number | null;
    /** Quoted price before tax. Absent on dashboards saved before this existed. */
    revenue?: number | null;
  };
  monthlyBreakdown: {
    monthStart: string;
    monthLabel: string;
    employeeCosts: number;
    projectCosts: number;
    totalCost: number;
    totalHours: number;
  }[];
  savedAt: string;
}

export interface SaveDashboardResponse {
  id: number;
  projectId: number;
  ownerId?: number;
  savedAt: string;
  message: string;
}

export interface SavedDashboardSummary {
  id: number;
  projectId: number;
  label?: string;
  projectName?: string;
  customerName?: string;
  ownerId?: number;
  memberCount: number;
  costCount: number;
  savedAt: string;
  calculatedCosts: DashboardData['calculatedCosts'];
}

export interface SavedDashboardsResponse {
  dashboards: SavedDashboardSummary[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
  };
}

export class DashboardService {
  /**
   * Save dashboard data to the database
   */
  static async saveDashboardData(dashboardData: DashboardData): Promise<SaveDashboardResponse> {
    try {
      const response = await apiClient.post<SaveDashboardResponse>('/dashboard/save', dashboardData);
      return response.data;
    } catch (error) {
      console.error('Error saving dashboard data:', error);
      throw error;
    }
  }

  /**
   * Get saved dashboard data for a project
   */
  static async getDashboardData(projectId: number): Promise<DashboardData | null> {
    try {
      const response = await apiClient.get<DashboardData>(`/dashboard/project/${projectId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      return null;
    }
  }

  /**
   * Get dashboard by dashboard ID
   */
  static async getDashboardById(dashboardId: number): Promise<DashboardData | null> {
    try {
      const response = await apiClient.get<DashboardData>(`/dashboard/${dashboardId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching dashboard by ID:', error);
      return null;
    }
  }

  /**
   * Get all saved dashboard sessions for a project
   */
  static async getDashboardHistory(projectId: number): Promise<DashboardData[]> {
    try {
      const response = await apiClient.get<DashboardData[]>(`/dashboard/project/${projectId}/history`);
      return response.data;
    } catch (error) {
      console.error('Error fetching dashboard history:', error);
      return [];
    }
  }

  /**
   * Get saved dashboards for current user
   */
  static async getSavedDashboards(limit = 50, offset = 0): Promise<SavedDashboardsResponse | null> {
    try {
      const response = await apiClient.get<SavedDashboardsResponse>('/dashboard/all', {
        params: { limit, offset },
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching saved dashboards:', error);
      return null;
    }
  }

  /**
   * Delete a saved dashboard session
   */
  static async deleteDashboardData(dashboardId: number): Promise<void> {
    try {
      await apiClient.delete(`/dashboard/${dashboardId}`);
    } catch (error) {
      console.error('Error deleting dashboard data:', error);
      throw error;
    }
  }
}
